/**
 * AboutDependencies - "Software and support dates".
 *
 * (a) This front end's own dependencies, embedded at build time (vite-plugin.ts).
 * (b) Each backend's GET /api/health/about, fetched live when this view mounts.
 * Every support status is written out in words; colour and icon only repeat it.
 * The EOL catalog is bundled - nothing is fetched from the internet.
 *
 * Contract: docs/standards/about-dependencies-contract.md.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BackendAbout, EolRow, EolStatusValue, FrontendBuildInfo } from './types';
import { evaluateFrontend, evaluatePackage, todayIso } from './eol';
import { badgeText, catalogFreshness, formatDate } from './format';
import { getBuildInfo } from './buildInfo';
import './AboutDependencies.css';

export interface AboutBackend {
  /** Shown as the section heading, e.g. "Retail API (pcb-retail-api)". */
  label: string;
  /** Calls GET /api/health/about with whatever auth the app already uses. */
  load: () => Promise<unknown>;
}

export interface AboutDependenciesProps {
  backends: AboutBackend[];
  /** Override for tests; defaults to the plugin-embedded build info. */
  build?: FrontendBuildInfo | null;
  /** Override for tests (YYYY-MM-DD). */
  today?: string;
  /** Tighter spacing for popovers. */
  compact?: boolean;
}

function StatusIcon({ status }: { status: EolStatusValue }): JSX.Element {
  // Shape differs per status as well as colour (check / clock / cross / question).
  const common = { width: 14, height: 14, viewBox: '0 0 16 16', 'aria-hidden': true, focusable: false } as const;
  switch (status) {
    case 'supported':
      return (
        <svg {...common}><circle cx="8" cy="8" r="7" fill="currentColor" /><path d="M4.5 8.2l2.2 2.2 4.8-4.8" stroke="#fff" strokeWidth="1.8" fill="none" /></svg>
      );
    case 'ending-soon':
      return (
        <svg {...common}><circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.6" fill="none" /><path d="M8 4.5V8l2.5 1.6" stroke="currentColor" strokeWidth="1.6" fill="none" /></svg>
      );
    case 'unsupported':
      return (
        <svg {...common}><circle cx="8" cy="8" r="7" fill="currentColor" /><path d="M5.3 5.3l5.4 5.4M10.7 5.3l-5.4 5.4" stroke="#fff" strokeWidth="1.8" /></svg>
      );
    default:
      return (
        <svg {...common}><circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.4" fill="none" /><path d="M6.2 6.2a1.9 1.9 0 1 1 2.6 1.8c-.6.3-.8.6-.8 1.2" stroke="currentColor" strokeWidth="1.4" fill="none" /><circle cx="8" cy="11.6" r=".9" fill="currentColor" /></svg>
      );
  }
}

export function EolBadge({ row }: { row: Pick<EolRow, 'status' | 'supportEnds' | 'supportEndsMonth' | 'daysRemaining'> }): JSX.Element {
  return (
    <span className={`pcb-about__badge pcb-about__badge--${row.status}`}>
      <StatusIcon status={row.status} />
      <span>{badgeText(row)}</span>
    </span>
  );
}

function SourceLink({ href }: { href?: string | null }): JSX.Element | null {
  if (!href) return null;
  return (
    <a className="pcb-about__source" href={href} target="_blank" rel="noopener noreferrer">
      Source
    </a>
  );
}

function EolList({ rows }: { rows: EolRow[] }): JSX.Element {
  if (rows.length === 0) return <p className="pcb-about__muted">No tracked components.</p>;
  return (
    <ul className="pcb-about__list">
      {rows.map((r) => (
        <li key={`${r.component}|${r.version}`} className="pcb-about__row">
          <span className="pcb-about__name">
            {r.component} <span className="pcb-about__ver">{r.version}</span>
          </span>
          <EolBadge row={r} />
          {(r.note || r.source) && (
            <span className="pcb-about__note">
              {r.note}
              {r.note && r.source ? ' ' : null}
              <SourceLink href={r.source ?? null} />
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function isBackendAbout(x: unknown): x is BackendAbout {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o.app === 'string' && typeof o.runtime === 'object' && Array.isArray(o.eol) && Array.isArray(o.packages);
}

type LoadState = { kind: 'loading' } | { kind: 'ok'; data: BackendAbout } | { kind: 'error'; message: string };

function BackendSection({ backend, today, reload }: { backend: AboutBackend; today: string; reload: number }): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [tick, setTick] = useState(0);
  // Parents pass a fresh object every render; fetch on label / retry / refresh only.
  const loadRef = useRef(backend.load);
  loadRef.current = backend.load;

  useEffect(() => {
    let live = true;
    setState({ kind: 'loading' });
    loadRef
      .current()
      .then((res) => {
        if (!live) return;
        if (isBackendAbout(res)) setState({ kind: 'ok', data: res });
        else setState({ kind: 'error', message: 'The server answered, but not with a dependency report. It may be running a build from before this feature.' });
      })
      .catch((e: unknown) => {
        if (!live) return;
        const msg = e instanceof Error ? e.message : String(e);
        setState({ kind: 'error', message: `Could not reach the server's dependency report (${msg}).` });
      });
    return () => {
      live = false;
    };
  }, [backend.label, tick, reload]);

  return (
    <section className="pcb-about__section" aria-busy={state.kind === 'loading'}>
      <h4 className="pcb-about__h">{backend.label}</h4>
      {state.kind === 'loading' && <p className="pcb-about__muted">Checking the server…</p>}
      {state.kind === 'error' && (
        <div className="pcb-about__alert pcb-about__alert--error" role="alert">
          {state.message}{' '}
          <button type="button" className="pcb-about__btn" onClick={() => setTick((t) => t + 1)}>
            Try again
          </button>
        </div>
      )}
      {state.kind === 'ok' && <BackendDetail data={state.data} today={today} />}
    </section>
  );
}

function BackendDetail({ data, today }: { data: BackendAbout; today: string }): JSX.Element {
  const rt = data.runtime;
  const fn = data.functions;
  return (
    <>
      {rt.matchesTarget === false && (
        <div className="pcb-about__alert pcb-about__alert--warn" role="alert">
          Warning: this server is running {rt.frameworkDescription}, but the deployed build targets {rt.targetFramework ?? rt.runtimeTarget}.
        </div>
      )}
      <dl className="pcb-about__facts">
        <div><dt>App</dt><dd>{data.app} v{data.version}</dd></div>
        <div><dt>Running on</dt><dd>{rt.frameworkDescription}{rt.targetFramework ? ` (built for ${rt.targetFramework})` : ''}</dd></div>
        <div>
          <dt>Functions</dt>
          <dd>
            worker {fn.workerVersion ?? 'not reported'} · SDK {fn.workerSdkVersion ?? 'not reported'}
            {fn.hostVersionSetting ? ` · host setting ${fn.hostVersionSetting}` : ''}
          </dd>
        </div>
        <div><dt>OS</dt><dd>{data.os.description} ({data.os.architecture})</dd></div>
        <div><dt>Support dates</dt><dd>Catalog {data.catalog.version}, {catalogFreshness(data.catalog.lastReviewed, today)}</dd></div>
      </dl>
      <EolList rows={data.eol} />
      <details className="pcb-about__details">
        <summary>
          Packages: {data.packageCounts.direct} chosen directly ({data.packageCounts.total} including the ones they bring in)
        </summary>
        <ul className="pcb-about__plain">
          {data.packages.map((p) => (
            <li key={p.name}>
              {p.name} <span className="pcb-about__ver">{p.version}</span>
            </li>
          ))}
        </ul>
      </details>
      {data.warnings.length > 0 && (
        <ul className="pcb-about__warnings">
          {data.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </>
  );
}

function FrontendSection({ build, today }: { build: FrontendBuildInfo | null; today: string }): JSX.Element {
  const rows = useMemo(() => (build ? evaluateFrontend(build, today) : []), [build, today]);
  if (!build) {
    return (
      <section className="pcb-about__section">
        <h4 className="pcb-about__h">This app</h4>
        <div className="pcb-about__alert pcb-about__alert--warn" role="alert">
          This build does not carry its dependency list (the About build step did not run).
        </div>
      </section>
    );
  }
  return (
    <section className="pcb-about__section">
      <h4 className="pcb-about__h">This app</h4>
      <dl className="pcb-about__facts">
        <div><dt>App</dt><dd>{build.app} v{build.version}</dd></div>
        <div><dt>Built</dt><dd>{formatDate(build.builtAt)} {build.builtAt.slice(11, 16)} UTC, with Node.js {build.nodeVersion}</dd></div>
        <div><dt>Support dates</dt><dd>Catalog {build.catalog.catalogVersion}, {catalogFreshness(build.catalog.lastReviewed, today)}</dd></div>
      </dl>
      <EolList rows={rows} />
      <details className="pcb-about__details">
        <summary>Packages in this build ({build.packages.length})</summary>
        <ul className="pcb-about__list">
          {build.packages.map((p) => {
            const r = evaluatePackage(build.catalog, p.name, p.version, today);
            return (
              <li key={p.name} className="pcb-about__row">
                <span className="pcb-about__name">
                  {p.name} <span className="pcb-about__ver">{p.version ?? `not in lockfile (${p.declared})`}</span>
                  {p.role === 'build' && <span className="pcb-about__tag">build tool</span>}
                </span>
                <EolBadge row={r ?? { status: 'unknown', supportEnds: null, supportEndsMonth: null, daysRemaining: null }} />
              </li>
            );
          })}
        </ul>
      </details>
    </section>
  );
}

export function AboutDependencies({ backends, build, today, compact = false }: AboutDependenciesProps): JSX.Element {
  const info = build === undefined ? getBuildInfo() : build;
  const day = today ?? todayIso();
  const [reload, setReload] = useState(0);
  const refresh = useCallback(() => setReload((r) => r + 1), []);
  return (
    <div className={`pcb-about${compact ? ' pcb-about--compact' : ''}`}>
      <FrontendSection build={info} today={day} />
      {backends.map((b) => (
        <BackendSection key={b.label} backend={b} today={day} reload={reload} />
      ))}
      {backends.length > 0 && (
        <div className="pcb-about__actions">
          <button type="button" className="pcb-about__btn" onClick={refresh}>
            Check the servers again
          </button>
        </div>
      )}
    </div>
  );
}

export default AboutDependencies;
